import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { deleteMediaAsync } from "@/store/redux/slices/media/media_slice";
import { Table } from "@/config/antd";
import Avatar from "@/components/common/Avatar";
import PropTypes from "prop-types";
import Column from "antd/es/table/Column";
import SingleMediaPhoto from "@/pages/media_manager/Single_Media_Photo";
import Delete from "@/components/common/alerts/Delete";
import WebIcon from "@/components/common/Web_Icons";
import MediaDialog from "@/pages/media_manager/components/Media_Dialog";
import { useLoader } from "@/hooks/LoaderProvider";
import { selectMediaIsLoading } from "@/store/redux/slices/media/media_slice";
import { selectSearchTerm } from "@/store/redux/slices/search/search_slice";

const MediaHomeTable = ({
  setSelectedFiles,
  data,
  onDelete,
  onDeleteSuccess,
}) => {
  const dispatch = useDispatch();
  const { showLoader, hideLoader } = useLoader();
  const [isMediaDialogOpen, setIsMediaDialogOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const isLoading = useSelector(selectMediaIsLoading);
  const globalSearchTerm = useSelector(selectSearchTerm) || "";

  // Apply global search filter
  const filteredData = globalSearchTerm
    ? data.filter(
        (item) =>
          item.title?.toLowerCase().includes(globalSearchTerm.toLowerCase()) ||
          item.description
            ?.toLowerCase()
            .includes(globalSearchTerm.toLowerCase()) ||
          item.author?.toLowerCase().includes(globalSearchTerm.toLowerCase())
      )
    : data;

  const handleOpenModal = (record) => {
    // Transform the record for SingleMediaPhoto
    const transformedData = {
      title: record?.title || record?.name, // Handle both title and name
      description: record?.description,
      date: record?.date || record?.createdAt,
      author: record?.author || record?.userID,
      authorAvatar: record?.authorAvatar,
      imageFiles: record?.imageFiles || [],
      videoFiles: record?.videoFiles || [],
      documentFiles: record?.documentFiles || [],
    };

    setSelectedMedia(transformedData);
    setIsMediaDialogOpen(true);
  };

  const handleDelete = (e, record) => {
    e.stopPropagation();
    // Call the parent component's delete function
    onDelete(record.key, record.title);
  };

  const handleEdit = (e, record) => {
    e.stopPropagation();

    // Transform the record for MediaDialog
    const transformedData = {
      _id: record?.key,
      name: record?.title || record?.name,
      description: record?.description,
      imageFiles: record?.imageFiles || [],
      videoFiles: record?.videoFiles || [],
      documentFiles: record?.documentFiles || [],
      userID: record?.author || record?.userID,
      createdAt: record?.date || record?.createdAt,
    };

    setSelectedMedia(transformedData);
    setShowEditDialog(true);
  };

  const handleDeleteConfirm = async () => {
    setIsDeleteModalOpen(false);
    showLoader();
    try {
      await dispatch(deleteMediaAsync(selectedMedia._id)).unwrap();
      hideLoader();
      // Call the parent's onDeleteSuccess handler if provided
      if (typeof onDeleteSuccess === "function") {
        onDeleteSuccess();
      }
    } catch (error) {
      console.error("Failed to delete media:", error);
      hideLoader();
    }
  };

  const truncateText = (text, maxLength = 45) => {
    return text?.length > maxLength
      ? text.substring(0, maxLength) + "..."
      : text;
  };

  const rowSelection = {
    onChange: (selectedRowKeys) => {
      setSelectedFiles(selectedRowKeys);
    },
    getCheckboxProps: (record) => ({
      disabled: record.name === "Disabled User",
      name: record.name,
    }),
  };

  const getFileTypeIcon = (record) => {
    if (record?.imageFiles?.length > 0) {
      return "ImageUpload";
    } else if (record?.videoFiles?.length > 0) {
      return "VideoUpload";
    } else if (record?.documentFiles?.length > 0) {
      return "DocumentUpload";
    }
    return "PDF"; // Default fallback
  };

  // Return null if there's no data
  if (!data || data.length === 0) {
    return null;
  }

  return (
    <>
      <Table
        loading={isLoading}
        rowSelection={{
          type: "checkbox",
          ...rowSelection,
        }}
        dataSource={filteredData}
        rowKey="id">
        <Column
          title="File Type"
          key="type"
          dataIndex="type"
          render={(_, record) => (
            <div
              className="flex items-center justify-center flex-shrink-0 w-12 h-12 rounded-lg bg-secondary cursor-pointer"
              onClick={() => handleOpenModal(record)}>
              <WebIcon icon={getFileTypeIcon(record)} size="large" />
            </div>
          )}
        />
        <Column
          title="File Name"
          key="title"
          dataIndex="title"
          render={(_, record) => (
            <p
              className="font-bold text-regular cursor-pointer"
              onClick={() => handleOpenModal(record)}>
              {truncateText(record.title)}
            </p>
          )}
        />
        <Column
          title="Description"
          key="description"
          dataIndex="description"
          width={180}
          render={(_, record) => (
            <div onClick={() => handleOpenModal(record)}>
              <p className="text-regular truncate w-[100px] cursor-pointer">
                {truncateText(record.description)}
              </p>
            </div>
          )}
        />
        <Column
          title="Uploader"
          key="author"
          dataIndex="author"
          render={(_, record) => (
            <div
              className="flex items-center gap-2 cursor-pointer"
              onClick={() => handleOpenModal(record)}>
              <Avatar
                src={record.authorAvatar}
                alt={record.author || "Uploader"}
                size={40}
              />
              <p className="font-bold text-regular whitespace-nowrap">
                {record.author}
              </p>
            </div>
          )}
        />
        <Column
          title="Date Uploaded"
          key="date"
          dataIndex="date"
          render={(_, record) => (
            <p
              className="font-bold text-regular cursor-pointer"
              onClick={() => handleOpenModal(record)}>
              {record.date}
            </p>
          )}
        />
        <Column
          title="Actions"
          dataIndex="options"
          key="options"
          render={(_, record) => (
            <div
              className="flex items-center gap-2 cursor-pointer"
              onClick={(e) => e.stopPropagation()}>
              <button
                onClick={(e) => handleEdit(e, record)}
                className="p-2  rounded-full transition-colors ">
                <WebIcon icon="Edit" className="w-[18px] " />
              </button>
              <button
                onClick={(e) => handleDelete(e, record)}
                className="p-2  rounded-full transition-colors">
                <WebIcon icon="Trash" className="w-[18px]" />
              </button>
            </div>
          )}
        />
      </Table>

      <SingleMediaPhoto
        isVisible={isMediaDialogOpen}
        onClose={() => {
          setIsMediaDialogOpen(false);
          setSelectedMedia(null);
        }}
        mediaData={selectedMedia}
      />

      <MediaDialog
        isVisible={showEditDialog}
        onClose={() => {
          setShowEditDialog(false);
          setSelectedMedia(null);
        }}
        mediaData={selectedMedia}
      />

      <Delete
        isVisible={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onProceed={handleDeleteConfirm}
        header="Delete Media"
        paragraph="Are you sure you want to delete this media? This action cannot be undone."
        proceedButtonText="Delete"
        cancelButtonText="Cancel"
      />
    </>
  );
};

MediaHomeTable.propTypes = {
  setSelectedFiles: PropTypes.func,
  data: PropTypes.array,
  onDelete: PropTypes.func,
  onDeleteSuccess: PropTypes.func,
};

export default MediaHomeTable;
